/**
 * NodePriceBadge — Envío 4: precio dinámico visible en el nodo.
 *
 * Mismo patrón que NodeChip/NodeBusyBadge: lo monta withNodeTooltip para los
 * 25+ nodos sin tocar ninguno. Pide /credits/quote con debounce de 400 ms
 * cuando cambian los datos del nodo (duración, resolución, entradas). Con el
 * flag apagado el backend devuelve el precio fijo actual, así que el precio
 * que ve el usuario SIEMPRE sale del mismo endpoint.
 *
 * Modos: exact/fixed → "19" · max → "hasta 726" (se cobra lo real) ·
 * blocked → "sin precio".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Coins } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { useCreditsStore } from '../../store/creditsStore';
import { useToolsStore } from '../../store/toolsStore';
import { priceParams } from '../../utils/priceParams';

export default function NodePriceBadge({ nodeId, toolId }) {
  const resultType = useToolsStore((s) => s.toolsById[toolId]?.result_type);
  const fetchQuote = useCreditsStore((s) => s.fetchQuote);
  const data = useWorkflowStore(
    (s) => s.nodes.find((n) => n.id === nodeId)?.data
  );
  const [quote, setQuote] = useState(null);
  const timer = useRef(null);

  const params = useMemo(() => priceParams(data), [data]);

  useEffect(() => {
    if (!resultType) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const q = await fetchQuote(resultType, params);
      setQuote(q);
    }, 400);
    return () => clearTimeout(timer.current);
  }, [resultType, params, fetchQuote]);

  if (!resultType || !quote || typeof quote.credits !== 'number') return null;

  const blocked = quote.mode === 'blocked';
  const isMax = quote.mode === 'max';
  const text = blocked ? 'sin precio' : isMax ? `hasta ${quote.credits}` : `${quote.credits}`;
  const title = blocked
    ? 'Esta combinación todavía no tiene precio'
    : isMax
      ? 'Reserva máxima: al terminar se cobra lo real medido, nunca más que esto'
      : `Costo en créditos${quote.reason ? ` — ${quote.reason}` : ''}`;

  return (
    <span
      className={`node-price-badge${blocked ? ' blocked' : ''}${isMax ? ' max' : ''}`}
      title={title}
      data-testid={`node-price-badge-${nodeId}`}
    >
      <Coins size={10} />
      {text}
    </span>
  );
}
